// One logline / plot field of an act or an episode (fragment), saved through
// the entity's own PUT with the atom nested in `text_atoms`. The four field
// hooks (`useUpdateActLogline`, `useUpdateActSynopsis`, `useUpdateFragment*`)
// differ only in the entity and the atom type; what they share is how the
// editor's list keeps up with a save that takes seconds (#1841, 22.09 item 4):
//
//   · the list takes the words as the save LEAVES — a header row that
//     remounts meanwhile seeds from the list, and would seed the old text;
//   · a list read already on the wire is cancelled — it was asked before these
//     words existed, and landing on top of them is the vanish;
//   · an atom the PUT created joins the list under the server's id — the next
//     keystroke's save names its atom from the list, and without an id it
//     would create a second one.

import { request } from '@/api/apiUtils';
import { actsEntity } from '@/data/entities/acts';
import { fragmentsEntity } from '@/data/entities/fragments';
import { getPrefixes, useCustomMutation, useOptimisticPrefixPatch } from '@/data/mutations';
import type { textAtomTypeId } from '@/enums';
import { useRequiredAppSelector } from '@/hooks';
import type { IFountainAct, IFountainFragment, ResponseType } from '@/types';

type HeaderEntity = IFountainAct | IFountainFragment;

interface EntityTextSaveOptions<TParams> {
    entity: 'act' | 'fragment';
    action: 'updateLogline' | 'updateSynopsis';
    typeId: textAtomTypeId;
    entityIdOf: (params: TParams) => number;
}

export function useEntityTextSave<TParams extends { content: string; textAtomId?: number }>({
    entity,
    action,
    typeId,
    entityIdOf,
}: EntityTextSaveOptions<TParams>) {
    const projectId = useRequiredAppSelector('projectId');
    const listKey = (entity === 'act' ? actsEntity : fragmentsEntity).fetchers.list.key({ projectId });
    // `{ projectId }` without a version matches every versioned copy of the list.
    const list = useOptimisticPrefixPatch<HeaderEntity[]>(listKey);
    const patchEntity = (entityId: number, update: (item: HeaderEntity) => HeaderEntity) =>
        list.patch(old => old?.map(item => (item.id === entityId ? update(item) : item)));

    return useCustomMutation<TParams, ResponseType<HeaderEntity>>({
        mutationFn: async params => {
            const textAtom: { text_atom_type_id: number; content: string; id?: number } = {
                text_atom_type_id: typeId,
                content: params.content,
            };
            if (params.textAtomId) textAtom.id = params.textAtomId;

            return request<ResponseType<HeaderEntity>>(`projects/${projectId}/${entity}s/${entityIdOf(params)}?_method=PUT`, {
                method: 'POST',
                body: JSON.stringify({ text_atoms: [textAtom] }),
            });
        },
        onMutate: async params => {
            await list.cancelInFlight();
            const { content, textAtomId } = params;
            patchEntity(entityIdOf(params), item => ({
                ...item,
                text_atoms: item.text_atoms?.map(atom => (atom.id === textAtomId ? { ...atom, content } : atom)),
            }));
        },
        // Atoms the list already holds keep the list's text: a later save of
        // the same field may be on the wire, and this answer predates it.
        onSuccess: (response, params) => {
            patchEntity(entityIdOf(params), item => {
                const known = new Set(item.text_atoms?.map(atom => atom.id));
                const created = response.data?.text_atoms?.filter(atom => atom.text_atom_type_id === typeId && !known.has(atom.id));
                return created?.length ? { ...item, text_atoms: [...(item.text_atoms ?? []), ...created] } : item;
            });
        },
        invalidate: [...getPrefixes(entity, action), listKey],
    });
}
